import { inject, Injectable } from '@angular/core';
import { IdbSystem2d, System2d } from '../asset/asset.dto';
import { logger, ChangeTyped, ID_FIELD_OPTIONS, IndexedService, SYSTEM2DS_STORE, SYSTEMS_COLLECTION, WW_WAREHOUSES_COLLECTION } from '@damofire/data-commons';
import { LockingIndexedService } from '../indexed/locking-indexed.service';
import { collection, collectionData, getFirestore, orderBy, query, where } from '@angular/fire/firestore';
import { NetworkService, ServiceEventType } from '@damofire/data-angular-commons';
import { generateAndAssignSystemIds, SystemDrawingService } from './system.service';
import { firstValueFrom } from 'rxjs';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { AuthDtoSignerService } from '@damofire/data-security';

@Injectable()
export class IdbSystemDrawingService extends SystemDrawingService {
  private readonly warehouseSyncEventService: WarehouseSyncEventService = inject(WarehouseSyncEventService);
  private readonly dtoSigner: AuthDtoSignerService = inject(AuthDtoSignerService);
  protected readonly lockingIndexedService: LockingIndexedService = inject(LockingIndexedService);
  protected readonly idbService: IndexedService = inject(IndexedService);
  protected readonly networkService: NetworkService = inject(NetworkService);

  constructor() {
    super();
  }

  get(division: string, system: string): Promise<System2d> {
    return this.lockingIndexedService.getByKey(division, SYSTEM2DS_STORE, [division, system]);
  }

  async reloadSystems(): Promise<void> {
    const divisionId = this.divisionId();
    const section = this.section();
    logger.debug('Searching for warehouse systems', { divisionId, category: 'system' });

    let systems: System2d[] = [];

    if (section) {
      systems = await this.lockingIndexedService
        .getAllFromIndex<IdbSystem2d>(divisionId, SYSTEM2DS_STORE, 'by-section-division', [section, divisionId])
        .catch(() => [] as IdbSystem2d[]);
    } else {
      // No section provided fall back to division-only index
      systems = await this.lockingIndexedService.getAllFromIndex<IdbSystem2d>(divisionId, SYSTEM2DS_STORE, 'division', divisionId).catch(() => [] as IdbSystem2d[]);
    }

    // Online fallback if nothing local
    if ((!systems || systems.length === 0) && this.networkService.connected) {
      logger.warn('Got an empty list of systems from local, fetching online', { divisionId, category: 'system' });
      const base = collection(getFirestore(), `${WW_WAREHOUSES_COLLECTION}/${divisionId}/${SYSTEMS_COLLECTION}`);
      const q = section ? query(base, where('section', '==', section), orderBy('number')) : query(base, orderBy('number'));
      const onlineSystems = await firstValueFrom(collectionData<System2d>(q, ID_FIELD_OPTIONS));
      this.systems.set(onlineSystems.slice().sort((s1: System2d, s2: System2d) => (s1.number || 0) - (s2.number || 0)));
    } else {
      this.systems.set(systems.slice().sort((s1: System2d, s2: System2d) => (s1.number || 0) - (s2.number || 0)));
    }
  }

  async create(divisionId: string, systems: System2d[]): Promise<void> {
    const systemsWithIds: System2d[] = generateAndAssignSystemIds(systems);

    for (const segment of systemsWithIds) {
      await this.dtoSigner.sign(segment, true);
      await this.idbService.add(SYSTEM2DS_STORE, { ...segment, divisionId, division: { _id: divisionId } });

      const changeTyped: ChangeTyped<System2d> = { ...segment, changeType: 'added' };
      this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: SYSTEM2DS_STORE });
      super.publishEvent(ServiceEventType.CREATED, segment);
    }
  }

  async delete(divisionId: string, segments: System2d[]): Promise<void> {
    const changes: ChangeTyped<System2d>[] = [];
    for (const segment of segments) {
      await this.idbService.delete(SYSTEM2DS_STORE, [divisionId, segment._id]);

      changes.push({ ...segment, changeType: 'removed' });
    }

    logger.info('Systems deleted', { count: segments.length, category: 'system' });
    super.publishEvent(ServiceEventType.DELETED, ...segments);
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes, store: SYSTEM2DS_STORE });
  }

  async update(divisionId: string, segments: System2d[]): Promise<void> {
    for (const segment of segments) {
      if (segment._id) {
        await this.dtoSigner.sign(segment, false, false);
        await this.idbService.update(SYSTEM2DS_STORE, { ...segment, divisionId });

        const changeTyped: ChangeTyped<System2d> = { ...segment, changeType: 'modified' };
        this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: SYSTEM2DS_STORE });
      }
    }
  }
}
